import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NzMessageService} from 'ng-zorro-antd/message';
import {CommonModule} from '@angular/common';
import {NzTableModule} from 'ng-zorro-antd/table';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzModalModule} from 'ng-zorro-antd/modal';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzSelectModule} from 'ng-zorro-antd/select';
import {UserService} from '../services/user.service';
import {NzIconDirective, NzIconModule} from 'ng-zorro-antd/icon';
import {NzPaginationModule} from 'ng-zorro-antd/pagination';
import {NzSwitchModule} from 'ng-zorro-antd/switch';
import {NzPopconfirmModule} from 'ng-zorro-antd/popconfirm';
import { forkJoin } from 'rxjs';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { DEFAULT_USER_PLAN, USER_PLAN_LABELS_EN, USER_PLANS, UserPlan } from '../models/user-plan.model';
import { User } from '../models/user.model';

const GENERATED_PASSWORD_LENGTH = 16;
const PASSWORD_UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const PASSWORD_LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const PASSWORD_DIGITS = '0123456789';
const PASSWORD_SPECIAL_CHARACTERS = '!@#$%&*';
const PASSWORD_CHARACTERS = `${PASSWORD_UPPERCASE}${PASSWORD_LOWERCASE}${PASSWORD_DIGITS}${PASSWORD_SPECIAL_CHARACTERS}`;

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzModalModule,
    NzFormModule,
    ReactiveFormsModule,
    FormsModule,
    NzSelectModule,
    NzIconDirective,
    NzPaginationModule,
    NzSwitchModule,
    NzPopconfirmModule,
    NzIconModule,
    NzCheckboxModule,
    NzTagModule,
    NzDatePickerModule,
    NzToolTipModule,
    TranslateModule
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.less'
})
export class UserManagementComponent implements OnInit{
  users: User[] = [];
  createUserForm!: FormGroup;
  editUserForm!: FormGroup;
  isCreateUserModalVisible = false;
  isEditUserModalVisible = false;
  generatedPassword: string | null = null;
  editingUser!: User;
  loading = false;

  paginatedUsers: User[] = [];
  allUsers: User[] = [];
  userPagination = { pageIndex: 1, pageSize: 5, total: 0 };
  userSort: { key: string; value: string | null } = { key: '', value: null };
  searchTerms: { email: string; displayName: string; companyName: string; customerNumber: string; contractNumber: string } = {
    email: '',
    displayName: '',
    companyName: '',
    customerNumber: '',
    contractNumber: ''
  };
  readonly userPlans = USER_PLANS;
  readonly defaultPlan: UserPlan = DEFAULT_USER_PLAN;
  readonly companyRoleTranslationKeys: Record<NonNullable<User['companyRole']>, string> = {
    company_admin: 'ROLES.COMPANY_ADMIN',
    editor: 'ROLES.EDITOR',
    viewer: 'ROLES.VIEWER',
  };

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private message: NzMessageService
  ) {}

  getCompanyRoleTranslationKey(user: User): string | null {
    if (user.role === 'Admin' || !user.companyRole) {
      return null;
    }

    return this.companyRoleTranslationKeys[user.companyRole] || null;
  }

  ngOnInit(): void {
    this.createUserForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      companyName: ['', Validators.required],
      maxProperties: ['', [Validators.required]],
      appMode: ['emergency', [Validators.required]],
      isTestAccount: [false],
      customerNumber: [''],
      contractNumber: [''],
      contractStartDate: [null],
      plan: [DEFAULT_USER_PLAN, [Validators.required]],
    });

    this.editUserForm = this.fb.group({
      maxProperties: ['', [Validators.required]],
      isTestAccount: [false],
      customerNumber: [''],
      contractNumber: [''],
      contractStartDate: [null],
      plan: [DEFAULT_USER_PLAN, [Validators.required]],
    });

    this.setupTestAccountToggle(this.createUserForm);
    this.setupTestAccountToggle(this.editUserForm);

    this.setupContractStartDateRules(this.createUserForm);
    this.setupContractStartDateRules(this.editUserForm);

    this.loadUsers();
  }

  deleteUser(user: any): void {
    if (user.role === 'Admin') {
      this.message.warning('Admin users cannot be deleted.');
      return;
    }

    this.loading = true;
    this.userService.deleteUser(user._id).subscribe({
      next: () => {
        this.message.success(`User ${user.email} deleted successfully.`);
        this.loadUsers();
      },
      error: (error) => {
        this.loading = false;
        this.message.error(error?.message || 'Failed to delete user.');
      }
    });
  }

  // Fetch users from the backend
  loadUsers() {
    this.loading = true;
    this.userService.getUsers().subscribe(
      (users) => {
        this.allUsers = users.map((user) => ({ ...user, plan: user?.plan || DEFAULT_USER_PLAN }));
        this.applyUserPagination();
        this.loading = false;
      },
      (error) => {
        this.message.error('Failed to load users.');
        this.loading = false;
      }
    );
  }

  onSearchDebounced = this.debounce(() => {
    this.onSearch();
  }, 300);

  debounce(func: Function, wait: number) {
    let timeout: any;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };
  }

  onSearch(): void {
    this.userPagination.pageIndex = 1;
    this.applyUserPagination();
  }

  onUserPageIndexChange(pageIndex: number): void {
    this.userPagination.pageIndex = pageIndex;
    this.applyUserPagination();
  }

  onUserPageSizeChange(pageSize: number): void {
    this.userPagination.pageSize = pageSize;
    this.userPagination.pageIndex = 1;
    this.applyUserPagination();
  }

  applyUserPagination(): void {
    // Filtering users based on search terms
    let filteredUsers = this.allUsers;
    const customerTerm = (this.searchTerms.customerNumber || '').toLowerCase().trim();
    const contractTerm = (this.searchTerms.contractNumber || '').toLowerCase().trim();

    filteredUsers = filteredUsers.filter((user) => {
      const emailMatch = (user.email || '')
        .toLowerCase()
        .includes((this.searchTerms.email || '').toLowerCase().trim());

      const nameMatch = (user.displayName || '')
        .toLowerCase()
        .includes((this.searchTerms.displayName || '').toLowerCase().trim());

      const companyMatch = (user.companyId?.name || '')
        .toLowerCase()
        .includes((this.searchTerms.companyName || '').toLowerCase().trim());

      const customerMatch = (user.customerNumber || '')
        .toLowerCase()
        .includes(customerTerm);

      const contractMatch = (user.contractNumber || '')
        .toLowerCase()
        .includes(contractTerm);

      return emailMatch && nameMatch && companyMatch && customerMatch && contractMatch;
    });

    // Sorting users if a sort order is specified
    if (this.userSort.key && this.userSort.value) {
      const sortOrder = this.userSort.value === 'ascend' ? 1 : -1;

      filteredUsers.sort((a, b) => {
        // sort lastLogin as date/time
        if (this.userSort.key === 'lastLogin') {
          const aTime = a?.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          const bTime = b?.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          return aTime < bTime ? -1 * sortOrder : aTime > bTime ? 1 * sortOrder : 0;
        }

        // sort createdAt as date/time (from company)
        if (this.userSort.key === 'createdAt') {
          const aTime = a?.companyId?.createdAt ? new Date(a.companyId.createdAt).getTime() : 0;
          const bTime = b?.companyId?.createdAt ? new Date(b.companyId.createdAt).getTime() : 0;
          return aTime < bTime ? -1 * sortOrder : aTime > bTime ? 1 * sortOrder : 0;
        }

        let aVal = (a as any)[this.userSort.key] || '';
        let bVal = (b as any)[this.userSort.key] || '';

        if (this.userSort.key === 'companyName') {
          aVal = a.companyId?.name || '';
          bVal = b.companyId?.name || '';
        }

        if (this.userSort.key === 'appMode') {
          aVal = a.companyId?.appMode === 'hospitality' ? 'hospitality' : 'emergency';
          bVal = b.companyId?.appMode === 'hospitality' ? 'hospitality' : 'emergency';
        }

        if (this.userSort.key === 'plan') {
          const planOrder: Record<UserPlan, number> = { free: 0, pro: 1, business: 2 };
          const aPlan = (a.plan || DEFAULT_USER_PLAN) as UserPlan;
          const bPlan = (b.plan || DEFAULT_USER_PLAN) as UserPlan;
          const aOrder = planOrder[aPlan] ?? 0;
          const bOrder = planOrder[bPlan] ?? 0;
          return aOrder < bOrder ? -1 * sortOrder : aOrder > bOrder ? 1 * sortOrder : 0;
        }

        return aVal < bVal ? -1 * sortOrder : aVal > bVal ? 1 * sortOrder : 0;
      });
    }

    // Updating pagination
    this.userPagination.total = filteredUsers.length;
    const start = (this.userPagination.pageIndex - 1) * this.userPagination.pageSize;
    const end = this.userPagination.pageIndex * this.userPagination.pageSize;
    this.paginatedUsers = filteredUsers.slice(start, end);
    this.users = filteredUsers.slice(start, end);
  }

  onUserSortChange(key: string, value: string | null): void {
    this.userSort = { key, value };
    this.applyUserPagination();
  }

  // Open the modal to create a user
  openCreateUserModal() {
    this.isCreateUserModalVisible = true;
  }

  // Close the modal
  closeCreateUserModal() {
    this.isCreateUserModalVisible = false;
    this.generatedPassword = null;
    this.createUserForm.reset();
    this.createUserForm.patchValue({ appMode: 'emergency', plan: DEFAULT_USER_PLAN, isTestAccount: false });
  }

  // Generate a cryptographically secure, policy-compliant initial password.
  generateRandomPassword(): string {
    const passwordCharacters = [
      this.randomPasswordCharacter(PASSWORD_UPPERCASE),
      this.randomPasswordCharacter(PASSWORD_LOWERCASE),
      this.randomPasswordCharacter(PASSWORD_DIGITS),
      this.randomPasswordCharacter(PASSWORD_SPECIAL_CHARACTERS),
    ];

    while (passwordCharacters.length < GENERATED_PASSWORD_LENGTH) {
      passwordCharacters.push(this.randomPasswordCharacter(PASSWORD_CHARACTERS));
    }

    for (let index = passwordCharacters.length - 1; index > 0; index -= 1) {
      const swapIndex = this.secureRandomIndex(index + 1);
      [passwordCharacters[index], passwordCharacters[swapIndex]] = [passwordCharacters[swapIndex], passwordCharacters[index]];
    }

    return passwordCharacters.join('');
  }

  // Create a user and company
  createUser() {
    if (this.createUserForm.valid) {
      const raw = this.createUserForm.getRawValue();
      const { email, companyName, maxProperties, appMode, isTestAccount, plan } = raw;

      let password: string;
      try {
        password = this.generateRandomPassword();
      } catch (_) {
        this.message.error('Unable to generate a secure password. Please try again.');
        return;
      }

      const cn = (raw.customerNumber || '').trim();
      const con = (raw.contractNumber || '').trim();

      const contractStartDateIso =
        !isTestAccount && con ? (raw.contractStartDate ? new Date(raw.contractStartDate).toISOString() : null) : null;

      const newUser = {
        email,
        companyName,
        password,
        maxProperties,
        appMode,
        isTestAccount: !!isTestAccount,
        customerNumber: isTestAccount ? null : (cn || null),
        contractNumber: isTestAccount ? null : (con || null),
        contractStartDate: contractStartDateIso,
        plan: plan || DEFAULT_USER_PLAN
      };

      this.userService.createUserWithCompany(newUser).subscribe({
        next: () => {
          this.message.success('User and Company created successfully');
          this.loadUsers(); // Refresh user list
          this.generatedPassword = password;
        },
        error: () => {
          this.message.error('Error creating user');
        }
      });
    } else {
      this.message.error('Please fill in all the required fields.');
      this.createUserForm.markAllAsTouched(); // Mark all fields as touched to show validation errors
    }
  }

  private randomPasswordCharacter(characters: string): string {
    return characters.charAt(this.secureRandomIndex(characters.length));
  }

  private secureRandomIndex(maxExclusive: number): number {
    const crypto = globalThis.crypto;
    if (!crypto?.getRandomValues || !Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new Error('Secure random generation is unavailable');
    }

    const upperBound = 0x100000000;
    const unbiasedLimit = upperBound - (upperBound % maxExclusive);
    const values = new Uint32Array(1);
    do {
      crypto.getRandomValues(values);
    } while (values[0] >= unbiasedLimit);

    return values[0] % maxExclusive;
  }

  // Open edit user modal to update max properties
  editUser(user: any) {
    this.editingUser = user;
    this.editUserForm.patchValue({
      maxProperties: user.companyId?.maxProperties || '',
      isTestAccount: !!user.isTestAccount,
      customerNumber: user.customerNumber || '',
      contractNumber: user.contractNumber || '',
      contractStartDate: user.contractStartDate ? new Date(user.contractStartDate) : null,
      plan: user.plan || DEFAULT_USER_PLAN
    });
    this.isEditUserModalVisible = true;
  }

  // Close the edit user modal
  closeEditUserModal() {
    this.isEditUserModalVisible = false;
    this.editUserForm.reset();
    this.editUserForm.patchValue({ plan: DEFAULT_USER_PLAN, isTestAccount: false });
  }

  updateUser() {
    if (!this.editUserForm.valid) {
      this.message.error('Please fill in all the required fields.');
      this.editUserForm.markAllAsTouched();
      return;
    }

    const raw = this.editUserForm.getRawValue();
    const companyId = this.editingUser?.companyId?._id;
    const userId = this.editingUser?._id;
    if (!userId) {
      this.message.error('Invalid user.');
      return;
    }

    const cn = (raw.customerNumber || '').trim();
    const con = (raw.contractNumber || '').trim();

    const requests = [];

    // 1) existing endpoint (only if company exists)
    if (companyId) {
      requests.push(this.userService.updateCompanyMaxProperties(companyId, raw.maxProperties));
    }

    // 2) new endpoint
    const contractStartDateIso =
      !raw.isTestAccount && con ? (raw.contractStartDate ? new Date(raw.contractStartDate).toISOString() : null) : null;

    requests.push(
      this.userService.updateUserIdentifiers(userId, {
        isTestAccount: !!raw.isTestAccount,
        customerNumber: raw.isTestAccount ? null : (cn || null),
        contractNumber: raw.isTestAccount ? null : (con || null),
        contractStartDate: contractStartDateIso,
        plan: raw.plan || DEFAULT_USER_PLAN
      })
    );

    this.loading = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.message.success('User updated successfully');
        this.loadUsers();
        this.closeEditUserModal();
      },
      error: () => {
        this.loading = false;
        this.message.error('Error updating user');
      },
    });
  }

  updateCompanyStatus(companyId: string, isActive: boolean): void {
    this.userService.updateCompanyStatus(companyId, isActive).subscribe({
      next: () => {
        this.message.success(`Company status updated to ${isActive ? 'active' : 'inactive'}.`);
        this.loadUsers(); // Refresh user list
      },
      error: () => {
        this.message.error('Error updating company status.');
      },
    });
  }

  updateUserStatus(user: any, enabled: boolean): void {
    // enabled=true => isDeactivated=false
    const isDeactivated = !enabled;

    this.userService.updateUserDeactivatedStatus(user._id, isDeactivated).subscribe({
      next: () => {
        this.message.success(`User status updated to ${enabled ? 'enabled' : 'disabled'}.`);
        this.loadUsers();
      },
      error: (error) => {
        this.message.error(error?.message || 'Error updating user status.');
        this.loadUsers(); // refresh UI back to real state
      }
    });
  }

  private setupTestAccountToggle(form: FormGroup): void {
    const testCtrl = form.get('isTestAccount');
    const custCtrl = form.get('customerNumber');
    const contCtrl = form.get('contractNumber');

    const apply = (isTest: boolean) => {
      if (isTest) {
        custCtrl?.setValue('', { emitEvent: false });
        contCtrl?.setValue('', { emitEvent: false });
        custCtrl?.disable({ emitEvent: false });
        contCtrl?.disable({ emitEvent: false });
      } else {
        custCtrl?.enable({ emitEvent: false });
        contCtrl?.enable({ emitEvent: false });
      }
    };

    apply(!!testCtrl?.value);
    testCtrl?.valueChanges.subscribe((v) => apply(!!v));
  }

  private setupContractStartDateRules(form: FormGroup): void {
    const testCtrl = form.get('isTestAccount');
    const contractCtrl = form.get('contractNumber');
    const startCtrl = form.get('contractStartDate');

    const apply = () => {
      const isTest = !!testCtrl?.value;
      const hasContract = !!String(contractCtrl?.value || '').trim();

      if (!isTest && hasContract) {
        startCtrl?.enable({ emitEvent: false });
      } else {
        startCtrl?.setValue(null, { emitEvent: false });
        startCtrl?.disable({ emitEvent: false });
      }
    };

    apply();
    testCtrl?.valueChanges.subscribe(apply);
    contractCtrl?.valueChanges.subscribe(apply);
  }

  getPlanLabel(plan?: UserPlan): string {
    const normalized = (plan || DEFAULT_USER_PLAN) as UserPlan;
    return USER_PLAN_LABELS_EN[normalized] || USER_PLAN_LABELS_EN[DEFAULT_USER_PLAN];
  }
}
